<script setup lang="ts">
import 'src/assets/ticket-edit-shared.css';
import { ref, computed, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { CreateTicketDTOType, FetchTicketResponsePriority } from 'src/api/models';
import type { CreateTicketDTO, CreateTicketDTOPriority } from 'src/api/models';
import { useToast } from 'primevue/usetoast';
import { useTicketService } from 'src/services/ticket-service';
import { useTrelloBoardStore } from 'src/stores/trello-board-store';
import { storeToRefs } from 'pinia';
import { toUTCDateTimeString } from 'src/utils/date-utc';
import type { AssigneeData } from 'src/services/assignee-service';
import TicketEditEntities from './TicketEditEntities.vue';
import TicketEditProperties from './TicketEditProperties.vue';
import TicketEditSectionHeader from './TicketEditSectionHeader.vue';

const props = defineProps<{
  initialScheduledDate?: string;
}>();

const emit = defineEmits<{
  'create-ticket': [id: string];
}>();

const modelValue = defineModel<boolean>();

const toast = useToast();
const { createTicket } = useTicketService();
const trelloBoardStore = useTrelloBoardStore();
const { ticketStatuses } = storeToRefs(trelloBoardStore);

const entitiesRef = ref<InstanceType<typeof TicketEditEntities>>();
const shouldValidate = ref(false);
const isSubmitting = ref(false);

const title = ref('');
const description = ref<string | null>(null);
const priority = ref<FetchTicketResponsePriority>(FetchTicketResponsePriority.medium);
const assignee = ref<AssigneeData | null>(null);


const scheduledDate = ref<Date | undefined>(parseScheduledDate());
const expectedDuration = ref<{ hours: number; minutes: number }>({ hours: 0, minutes: 0 });

function parseScheduledDate(): Date | undefined {
  if (!props.initialScheduledDate) return undefined;
  const now = new Date();
  const date = new Date(props.initialScheduledDate + 'T00:00:00');
  date.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
  return date;
}

function handleTitleUpdate(newTitle: string): void {
  title.value = newTitle;
}

function handleDescriptionUpdate(value: string): void {
  description.value = value;
}

function handlePriorityUpdate(value: FetchTicketResponsePriority): void {
  priority.value = value;
}

function handleAssigneeUpdate(value: AssigneeData | null): void {
  assignee.value = value;
}

function handleScheduledDateUpdate(date: Date | null): void {
  scheduledDate.value = date ?? undefined;
}

function handleExpectedDurationUpdate(dur: { hours: number; minutes: number }): void {
  expectedDuration.value = dur;
}

async function handleSubmit(): Promise<void> {
  if (isSubmitting.value) return;

  shouldValidate.value = true;

  if (!title.value) {
    return;
  }

  const newStatusId = ticketStatuses.value.find((status) => status.name === 'new')?.id;

  if (!newStatusId) {
    toast.add({
      summary: 'Error',
      severity: 'error',
      detail: "Could not find 'new' status",
      life: 3000,
    });
    return;
  }

  isSubmitting.value = true;

  try {
    const entities = entitiesRef.value;

    const dto: CreateTicketDTO = {
      title: title.value,
      type: CreateTicketDTOType.variable,
      description: description.value ?? undefined,
      priority: priority.value as unknown as CreateTicketDTOPriority,
      status_id: newStatusId,
      scheduled_date: scheduledDate.value ? toUTCDateTimeString(scheduledDate.value) : undefined,
      expected_duration: { hours: expectedDuration.value.hours, minutes: expectedDuration.value.minutes } as any,
      unit_id: entities?.unit?.id,
      assignee_id: assignee.value?.id ?? undefined,
      organisation_id: entities?.organisation?.id,
      site_id: entities?.site?.id,
      organisation_contact_person_id: entities?.contactPerson?.id,
      site_contact_person_id: entities?.unitContactPerson?.id,
    };

    const ticketId = await createTicket(dto);

    modelValue.value = false;
    emit('create-ticket', ticketId);
  } catch (error) {
    toast.add({
      summary: 'Error',
      severity: 'error',
      detail: error instanceof Error ? error.message : 'An error occurred while creating the ticket',
      life: 3000,
    });
  } finally {
    isSubmitting.value = false;
  }
}

function resetForm(): void {
  shouldValidate.value = false;
  isSubmitting.value = false;
  title.value = '';
  description.value = null;
  priority.value = FetchTicketResponsePriority.medium;
  assignee.value = null;
  scheduledDate.value = parseScheduledDate();
  expectedDuration.value = { hours: 0, minutes: 0 };
  entitiesRef.value?.reset();
}

watch(modelValue, (isOpen) => {
  if (isOpen) {
    scheduledDate.value = parseScheduledDate();
  } else {
    resetForm();
  }
});
</script>

<template>
  <Dialog
    v-model:visible="modelValue"
    :style="{ width: '85vw', maxWidth: '1200px' }"
    :breakpoints="{ '1766px': '90vw', '960px': '100vw' }"
    :content-style="{ height: '88vh' }"
    modal
    closable
    :pt="{
      header: { class: '!py-2 !px-8 border-b border-surface-200 dark:border-surface-700' },
      content: { class: '!p-0 !flex !flex-col !overflow-hidden' },
    }"
  >
    <template #header>
      <div class="flex items-center align-middle gap-3">

        <div>
          <span class="text-lg font-semibold text-surface-800 dark:text-surface-100">New Ticket</span>
        </div>
      </div>
    </template>

    <div class="flex-1 overflow-y-auto">
      <div class="flex min-h-0">
        <div class="flex-1 p-8">
          <section>
            <TicketEditEntities ref="entitiesRef" />
          </section>
        </div>

        <aside class="w-[300px] shrink-0 border-l border-surface-200 dark:border-surface-700 bg-surface-50/50 dark:bg-surface-800/20">
          <TicketEditProperties
            :title="title"
            @update:title="handleTitleUpdate"
            :description="description"
            @update:description="handleDescriptionUpdate"
            :should-validate-title="shouldValidate"
            :scheduled-date="scheduledDate"
            @update:scheduled-date="handleScheduledDateUpdate"
            :expected-duration="expectedDuration"
            @update:expected-duration="handleExpectedDurationUpdate"
            :priority="priority"
            @update:priority="handlePriorityUpdate"
            :assignee="assignee"
            @update:assignee="handleAssigneeUpdate"
          />
        </aside>
      </div>
    </div>

    <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-surface-200 dark:border-surface-700 bg-surface-card shrink-0">
      <Button type="button" label="Cancel" severity="secondary" text @click="modelValue = false" />
      <Button
        type="button"
        label="Create Ticket"
        severity="primary"
        icon="pi pi-check"
        :loading="isSubmitting"
        @click="handleSubmit"
      />
    </div>
  </Dialog>
</template>

